/**
 * Dates the way the client's Analys, Drift, Konton, Dokument and Notiser screens write them, since
 * the second build round of 2026-09-25.
 *
 * The moment in a list ("I dag 09:50", "21 sep 02:53") is `formatWhen` from `app/lib/adminFormat.ts`,
 * which is not admin-specific in anything but its name, and is re-exported here so the five screens
 * import their dates from one place. What is added is the day without a time, for a row whose times
 * are inside it, and the day of a `date` column read from its own parts.
 */

export { formatWhen } from "../../lib/adminFormat";

const shortDate = new Intl.DateTimeFormat("sv-SE", { day: "numeric", month: "short" });
const shortDateYear = new Intl.DateTimeFormat("sv-SE", { day: "numeric", month: "short", year: "numeric" });
const longDay = new Intl.DateTimeFormat("sv-SE", { day: "numeric", month: "long" });
const longDayYear = new Intl.DateTimeFormat("sv-SE", { day: "numeric", month: "long", year: "numeric" });

function parse(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  // A `date` column is built from its parts: `new Date("2026-09-03")` is UTC midnight, which every
  // timezone behind UTC renders as the day before.
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const parsed = day ? new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3])) : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** "21 sep", and the year only when it is not this one. Null for a missing or unreadable value. */
export function formatShortDay(value: string | null | undefined, now: Date = new Date()): string | null {
  const parsed = parse(value);
  if (!parsed) {
    return null;
  }
  return parsed.getFullYear() === now.getFullYear() ? shortDate.format(parsed) : shortDateYear.format(parsed);
}

/** "8 september", and the year only when it is not this one. For a period and a validity date. */
export function formatLongDay(value: string | null | undefined, now: Date = new Date()): string | null {
  const parsed = parse(value);
  if (!parsed) {
    return null;
  }
  return parsed.getFullYear() === now.getFullYear() ? longDay.format(parsed) : longDayYear.format(parsed);
}

/**
 * The moment with a lower-case first letter, for the middle of a sentence: "Hämtat i dag 06:00".
 * `formatWhen` starts "I dag" and "I går" with a capital because it usually stands alone in a row.
 */
export function inSentence(moment: string): string {
  return moment.charAt(0).toLowerCase() + moment.slice(1);
}
